<template>
  <div
    :class="[ 'mb-3 overflow-hidden rounded-xl border transition-all duration-200', item.read ? 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900' : 'border-primary/20 bg-primary/5', ]"
  >
    <!-- Collapsed row -->
    <div
      class="flex cursor-pointer items-center justify-between p-4 hover:bg-black/[0.02]"
      @click="$emit('click', item)"
    >
      <div class="flex min-w-0 items-center gap-3">
        <!-- Type-specific icon -->
        <div :class="['flex h-10 w-10 shrink-0 items-center justify-center rounded-full', iconBg]">
          <!-- Phone icon: incoming_call / end_call / scheduled_call -->
          <svg v-if="isCallType" :class="['h-5 w-5', iconColor]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
          <!-- Document icon: call_summary -->
          <svg v-else-if="isSummaryType" :class="['h-5 w-5', iconColor]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <!-- Bell icon: default / notification -->
          <svg v-else :class="['h-5 w-5', iconColor]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
        </div>

        <div class="flex min-w-0 flex-col">
          <div class="flex items-center gap-2">
            <h4 class="truncate text-sm font-semibold text-gray-900 dark:text-slate-100">
              {{ item.title || typeLabel }}
            </h4>
            <span v-if="!item.read" class="size-2 shrink-0 rounded-full bg-primary" />
          </div>
          <p class="mt-0.5 line-clamp-1 text-xs text-gray-500 dark:text-slate-400">{{ item.message }}</p>
        </div>
      </div>

      <div class="ml-3 flex shrink-0 items-center gap-1.5">
        <span class="text-xs text-gray-400">{{ formattedTime }}</span>
        <svg
          class="h-4 w-4 text-gray-400 transition-transform duration-200"
          :class="{ 'rotate-180': expanded }"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>

    <!-- Expanded body -->
    <div v-show="expanded" class="border-t border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-800 px-4 py-3">
      <p class="text-sm leading-relaxed text-gray-700 dark:text-slate-300">{{ item.message }}</p>
      <p v-if="fullDate" class="mt-2 text-xs text-gray-400">{{ fullDate }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { NotificationItem } from "~/types/notifications";

const props = defineProps<{
  item: NotificationItem;
  expanded?: boolean;
}>();

defineEmits<{
  (e: "click", item: NotificationItem): void;
}>();

const type = computed(() => props.item.type?.toLowerCase() ?? "");

const isCallType = computed(() =>
  ["incoming_call", "end_call", "scheduled_call"].includes(type.value),
);
const isSummaryType = computed(() => type.value === "call_summary");

// Icon background tint and foreground color per notification type.
const iconBg = computed(() => {
  if (isCallType.value) return "bg-green-100";
  if (isSummaryType.value) return "bg-purple-100";
  return "bg-gray-100 dark:bg-slate-800";
});

const iconColor = computed(() => {
  if (isCallType.value) return "text-green-600";
  if (isSummaryType.value) return "text-purple-500";
  return "text-gray-400";
});

/** Human-readable label derived from type when no title is set. */
const typeLabel = computed(() => {
  const map: Record<string, string> = {
    incoming_call: "Incoming Call",
    end_call: "Call Ended",
    call_summary: "Call Summary",
    scheduled_call: "Scheduled Call",
    notification: "Notification",
  };
  return map[type.value] ?? "Notification";
});

/**
 * Short relative time shown in the collapsed row:
 *  - Same day   → "09:15 AM"
 *  - ≤ 30 days  → "2d"
 *  - Older      → "Apr 10"
 */
const formattedTime = computed(() => {
  if (!props.item.createdAt) return "";
  const date = new Date(props.item.createdAt);
  if (isNaN(date.getTime())) return "";

  const now = new Date();
  const diffDays = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24),
  );
  const isSameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();

  if (isSameDay) {
    return date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  }
  if (diffDays <= 30) return `${diffDays}d`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
});

/** Full date shown in the expanded body. */
const fullDate = computed(() => {
  if (!props.item.createdAt) return "";
  const date = new Date(props.item.createdAt);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
});
</script>
